/**
 * Rng — Mulberry32, a tiny seeded PRNG. The BLESSED deterministic-random path
 * for the first future stochastic system (e.g. particle jitter, enemy variety).
 *
 * Why this exists now: the ESLint ban forbids `Math.random` in `src/engine/`, so
 * when randomness is first needed it must flow through a seeded, replay-safe
 * source. Mulberry32 uses only integer ops (`Math.imul`, shifts, `>>> 0`) — NO
 * transcendentals — so it is bit-identical across JS engines, like the rest of
 * the sim. Nothing in the sim consumes it today; it is not load-bearing yet.
 */
export class Rng {
  private s: number;

  constructor(seed: number) {
    // Force to a uint32 so float seeds / negatives behave.
    this.s = seed >>> 0;
  }

  /** Next float in [0, 1). */
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Integer in [0, n). */
  int(n: number): number {
    return (this.next() * n) | 0;
  }

  /** Float in [lo, hi). */
  range(lo: number, hi: number): number {
    return lo + this.next() * (hi - lo);
  }

  /** Restore an exact stream position (for replay scrubbing). */
  setState(state: number): void { this.s = state >>> 0; }
  /** Current stream position. */
  getState(): number { return this.s; }
}
